/**
 * SelectField - Abstracted searchable select component
 *
 * A wrapper around MUI Autocomplete that provides a TextField+MenuItem-like API
 * but with built-in search/type functionality.
 *
 * Supports both simple string arrays and object arrays with custom option rendering.
 */

import React from 'react';
import { Autocomplete, TextField } from '@mui/material';

export interface SelectFieldOption {
  label: string;
  value: any;
}

export interface SelectFieldProps {
  label?: string;
  value?: any;
  onChange?: (event: any, value: any) => void;
  options: SelectFieldOption[] | string[] | any[];
  sx?: any;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  placeholder?: string;
  getOptionLabel?: (option: any) => string;
  getOptionValue?: (option: any) => any;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  allowEmpty?: boolean;
  emptyLabel?: string;
  [key: string]: any;
}

const SelectField = React.forwardRef<HTMLDivElement, SelectFieldProps>((props, ref) => {
  const {
    label,
    value,
    onChange,
    options = [],
    sx,
    disabled = false,
    required = false,
    error = false,
    helperText,
    placeholder,
    getOptionLabel,
    getOptionValue,
    fullWidth = false,
    size = 'medium',
    allowEmpty = true,
    emptyLabel = 'All',
    ...rest
  } = props;

  // Determine if options are simple strings or objects
  const isStringArray = options.length > 0 && typeof options[0] === 'string';

  // Build full options list (with empty option if allowed)
  const fullOptions = React.useMemo(() => {
    if (!allowEmpty) return options;

    if (isStringArray) {
      return ['', ...options];
    }

    // For object arrays, add empty option
    return [{ label: emptyLabel, value: '' }, ...options];
  }, [options, allowEmpty, isStringArray, emptyLabel]);

  // Find the selected option object from the value
  const selectedOption = React.useMemo(() => {
    if (value === '' || value === null || value === undefined) {
      return isStringArray ? '' : { label: emptyLabel, value: '' };
    }

    if (isStringArray) {
      return value;
    }

    // For object arrays, find matching option
    if (getOptionValue) {
      return fullOptions.find((opt) => getOptionValue(opt) === value) || null;
    }

    // Default: match by value property
    return fullOptions.find((opt) => opt.value === value) || null;
  }, [value, fullOptions, isStringArray, getOptionValue, emptyLabel]);

  // Handle option label display
  const handleGetOptionLabel = (option: any) => {
    if (!option) return '';

    if (typeof option === 'string') {
      return option === '' ? emptyLabel : option;
    }

    if (getOptionLabel) {
      return getOptionLabel(option);
    }

    return option.label || '';
  };

  // Handle change event
  const handleChange = (event: any, newValue: any) => {
    if (!onChange) return;

    // Extract the value to pass to onChange
    let extractedValue;

    if (newValue === null || newValue === '') {
      extractedValue = '';
    } else if (typeof newValue === 'string') {
      extractedValue = newValue;
    } else if (getOptionValue) {
      extractedValue = getOptionValue(newValue);
    } else {
      extractedValue = newValue.value;
    }

    // Create a synthetic event that matches TextField onChange signature
    const syntheticEvent = {
      ...event,
      target: {
        ...event.target,
        value: extractedValue,
      },
    };

    onChange(syntheticEvent, extractedValue);
  };

  // Check if two options are equal
  const isOptionEqualToValue = (option: any, val: any) => {
    if (!val) return !option || option === '' || option.value === '';

    if (typeof option === 'string' && typeof val === 'string') {
      return option === val;
    }

    if (getOptionValue) {
      const optionValue = getOptionValue(option);
      const valValue = typeof val === 'object' ? getOptionValue(val) : val;
      return optionValue === valValue;
    }

    return option.value === (typeof val === 'object' ? val.value : val);
  };

  return (
    <Autocomplete
      ref={ref}
      value={selectedOption}
      onChange={handleChange}
      options={fullOptions}
      getOptionLabel={handleGetOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      disabled={disabled}
      fullWidth={fullWidth}
      size={size}
      disableClearable
      sx={sx}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={error}
          helperText={helperText}
          placeholder={placeholder}
        />
      )}
      {...rest}
    />
  );
});

SelectField.displayName = 'SelectField';

export default SelectField;
